import React, {
  ChangeEvent,
  FormEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import GridOnIcon from '@mui/icons-material/GridOn';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { InvoiceItem, defaultInvoiceItem } from '../../config/types';
import { BRAND } from '../../theme';
import EntrySteps, { COMPACT_STEPS_HEIGHT } from './EntrySteps';
import ShortcutsBar from './ShortcutsBar';
import { ENTRY_SECTIONS, isFieldComplete, pickActiveSection } from './sections';

interface CustomerLocation {
  name: string;
  miles: string;
  address: string;
  cityStateZip: string;
}

/** Everything the form owns that is worth restoring after a refresh. */
export interface EntryFormDraftState {
  formData: InvoiceItem;
  pumpEnabled: boolean;
  pumpCount: string;
  baseDem: string;
  nextRowNumber: number;
}

interface InvoiceEntryFormProps {
  onSubmit: () => Promise<void>;
  broker: string;
  invoiceSheet: string;
  onAddEntry: (formData: InvoiceItem, calculatedEntry: InvoiceItem) => void;
  onUpdateEntry: (index: number, formData: InvoiceItem, calculatedEntry: InvoiceItem) => void;
  onDeleteEntry: (index: number) => void;
  onClearEntries: () => void;
  entries: InvoiceItem[];
  isSaving: boolean;
  /** Restored draft applied on first render only */
  initialDraft?: EntryFormDraftState | null;
  onDraftStateChange: (state: EntryFormDraftState) => void;
  onSaveDraft: () => void;
  onPreviewEntries: () => void;
  /** Phone layout only: switch the workspace over to the PDF view */
  onShowDocument?: () => void;
}

// Height of the app bar on phones; the step bar sticks right under it.
const MOBILE_APP_BAR_HEIGHT = 60;
// Space a section heading needs below the sticky chrome when jumped to.
const MOBILE_SCROLL_OFFSET = MOBILE_APP_BAR_HEIGHT + COMPACT_STEPS_HEIGHT + 12;

export interface InvoiceEntryFormRef {
  resetForm: () => void;
  setFormData: (data: InvoiceItem) => void; // Note: Always preserves existing rowNumber
  handleEditEntry: (index: number) => void;
  handleDeleteEntry: (index: number) => void;
}

const terminals = ['Tesoro', 'Kinder Morgan', 'Chevron', 'Jet Infrastructure'] as const;

// Total Dem = the manually entered base demurrage plus the pump count (when the
// pump charge is enabled). Each pump adds 1 to Total Dem.
const computeTotalDem = (base: string, pumpEnabled: boolean, pumpCount: string): string => {
  if (!pumpEnabled) return base;
  return String((parseFloat(base) || 0) + (parseFloat(pumpCount) || 0));
};

const customerLocations: CustomerLocation[] = [
  { name: 'Air Center San Diego', miles: '145', address: '1424 Continental St', cityStateZip: 'San Diego, CA 92154' },
  { name: 'Riverside Air Service', miles: '70', address: '6741 Gemende Dr', cityStateZip: 'Riverside, CA 92504' },
  { name: 'Flyers Enterprises @ Oceanside', miles: '75', address: '110 Jones Street', cityStateZip: 'Oceanside, CA 92054' },
  { name: 'Flyers Enterprises @ El Cajon', miles: '135', address: '1745 N Marshall Ave', cityStateZip: 'El Cajon, CA 92020' },
  { name: 'Archer Jet Center', miles: '10', address: '37232 W 120th', cityStateZip: 'Hawthorne, CA 90250' },
  { name: 'San Bernardino Airport', miles: '70', address: '2055 E Peremtier -R', cityStateZip: 'San Bernardino, CA 92407' },
  { name: 'Bakersfield Jet Center', miles: '135', address: '1601 Skydrive', cityStateZip: 'Bakersfield, CA 93308' }
];

/**
 * Scrolls a pane, then makes sure it landed. Some engines quietly ignore smooth
 * scrolling inside a nested scroll container, which would leave the step bar
 * looking broken, so the position is enforced once the animation should be over.
 */
const scrollPaneTo = (container: HTMLElement, top: number) => {
  container.scrollTo({ top, behavior: 'smooth' });
  window.setTimeout(() => {
    if (Math.abs(container.scrollTop - top) > 4) {
      container.scrollTop = top;
    }
  }, 300);
};

const SectionHeading: React.FC<{ id: string; title: string; first?: boolean }> = ({
  id,
  title,
  first
}) => (
  <Box id={id} sx={{ scrollMarginTop: 8, pt: first ? 0 : 2.5 }}>
    <Typography variant="subtitle2" sx={{ color: BRAND.navy, mb: 1.5 }}>
      {title}
    </Typography>
  </Box>
);

const InvoiceEntryForm = forwardRef<InvoiceEntryFormRef, InvoiceEntryFormProps>(
  (
    {
      onSubmit,
      broker,
      invoiceSheet,
      onAddEntry,
      onUpdateEntry,
      onDeleteEntry,
      onClearEntries,
      entries,
      isSaving,
      initialDraft,
      onDraftStateChange,
      onSaveDraft,
      onPreviewEntries,
      onShowDocument
    },
    ref
  ) => {
    const theme = useTheme();
    // Below `md` the form flows with the page (one scroll, sticky step bar and a
    // sticky action bar) instead of living in its own fixed-height pane.
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [formData, setFormData] = useState<InvoiceItem>(
      initialDraft?.formData || { ...defaultInvoiceItem, broker, invoiceSheet }
    );
    const [error, setError] = useState<string | null>(null);
    const [nextRowNumber, setNextRowNumber] = useState(initialDraft?.nextRowNumber ?? 5);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    // Pump charge: when enabled, the pump count is added into Total Dem. `baseDem`
    // holds the manually entered demurrage so toggling/changing pumps recomputes
    // Total Dem cleanly without double-counting.
    const [pumpEnabled, setPumpEnabled] = useState(initialDraft?.pumpEnabled ?? false);
    const [pumpCount, setPumpCount] = useState(initialDraft?.pumpCount ?? '1');
    const [baseDem, setBaseDem] = useState(initialDraft?.baseDem ?? defaultInvoiceItem.totalDem ?? '');
    const [activeSection, setActiveSection] = useState(0);
    const [generateMenuAnchor, setGenerateMenuAnchor] = useState<null | HTMLElement>(null);
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    // After a step is tapped, hold its highlight while the jump scroll settles
    // (near the bottom of the page a later section can't reach the top band).
    const holdActiveUntil = useRef(0);

    const resetPumpState = () => {
      setPumpEnabled(false);
      setPumpCount('1');
      setBaseDem('');
    };

    // Update form data when broker or invoiceSheet prop changes
    useEffect(() => {
      setFormData(prev => ({ ...prev, broker, invoiceSheet }));
    }, [broker, invoiceSheet]);

    // Hand the draftable slice of state up so the workspace can autosave it and
    // show the completion ring.
    useEffect(() => {
      onDraftStateChange({ formData, pumpEnabled, pumpCount, baseDem, nextRowNumber });
    }, [formData, pumpEnabled, pumpCount, baseDem, nextRowNumber, onDraftStateChange]);

    const completeSectionIds = useMemo(
      () =>
        ENTRY_SECTIONS.filter(
          section =>
            section.required.length > 0 &&
            section.required.every(field => isFieldComplete(formData, field))
        ).map(section => section.id),
      [formData]
    );

    /* ------------------------------ scroll spy ------------------------------- */

    // Watches the section headings rather than listening for scroll events, so
    // the step bar tracks the pane however it was moved.
    useEffect(() => {
      const container = scrollRef.current;
      if (!container || typeof IntersectionObserver === 'undefined') return undefined;

      // On phones the page itself scrolls, so watch the viewport just below the
      // sticky chrome; on desktop the form pane is the scroller.
      const options: IntersectionObserverInit = isMobile
        ? { root: null, rootMargin: `-${MOBILE_SCROLL_OFFSET}px 0px -55% 0px`, threshold: 0 }
        : // Only the top third of the pane counts as "current".
          { root: container, rootMargin: '0px 0px -67% 0px', threshold: 0 };

      const visible = new Set<string>();

      const observer = new IntersectionObserver(
        observerEntries => {
          observerEntries.forEach(entry => {
            if (entry.isIntersecting) visible.add(entry.target.id);
            else visible.delete(entry.target.id);
          });

          if (Date.now() < holdActiveUntil.current) return;
          const current = pickActiveSection(visible);
          if (current >= 0) setActiveSection(current);
        },
        options
      );

      ENTRY_SECTIONS.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) observer.observe(element);
      });

      return () => observer.disconnect();
    }, [isMobile]);

    const scrollToSection = (sectionId: string) => {
      const element = document.getElementById(sectionId);
      const container = scrollRef.current;
      if (!element || !container) return;
      // Light the step up on click rather than waiting for the scroll to settle.
      const index = ENTRY_SECTIONS.findIndex(section => section.id === sectionId);
      if (index >= 0) setActiveSection(index);
      holdActiveUntil.current = Date.now() + 900;
      if (isMobile) {
        const top = element.getBoundingClientRect().top + window.scrollY - MOBILE_SCROLL_OFFSET;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        return;
      }
      const offset =
        element.getBoundingClientRect().top - container.getBoundingClientRect().top;
      scrollPaneTo(container, container.scrollTop + offset - 8);
    };

    // Back to the first field, in whichever element is doing the scrolling.
    const scrollFormToTop = () => {
      const container = scrollRef.current;
      if (!container) return;
      if (isMobile) {
        const top = container.getBoundingClientRect().top + window.scrollY - MOBILE_SCROLL_OFFSET;
        if (window.scrollY > top) window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        return;
      }
      scrollPaneTo(container, 0);
    };

    /* ------------------------------- handlers -------------------------------- */

    const handleChange = (
      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
    ) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Editing Total Dem directly sets the base demurrage (only editable when the
    // pump charge is off); the field reflects base + pumps.
    const handleTotalDemChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setBaseDem(value);
      setFormData(prev => ({ ...prev, totalDem: computeTotalDem(value, pumpEnabled, pumpCount) }));
    };

    const handlePumpToggle = (e: ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setPumpEnabled(checked);
      setFormData(prev => ({ ...prev, totalDem: computeTotalDem(baseDem, checked, pumpCount) }));
    };

    const handlePumpCountChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setPumpCount(value);
      setFormData(prev => ({ ...prev, totalDem: computeTotalDem(baseDem, pumpEnabled, value) }));
    };

    const handleAddEntry = async (e: FormEvent) => {
      e.preventDefault();
      setError(null);

      try {
        // Calculate all values
        const freightRev = parseFloat(
          (parseFloat(formData.gross || '0') * parseFloat(formData.rate || '0')).toFixed(2)
        );
        const fuelSurcharge = parseFloat(
          (
            (parseFloat(freightRev.toString() || '0') *
              (parseFloat(formData.fuelSurchargePercent || '0') / 10)) /
            10
          ).toFixed(2)
        );
        const demurrageCharge = parseFloat(
          (parseFloat(formData.totalDem || '0') * parseFloat('100')).toFixed(2)
        );
        const totalCharges = parseFloat(
          (freightRev + fuelSurcharge + demurrageCharge).toFixed(2)
        );

        // Persist the pump charge as its own fields so it can later be displayed
        // separately from the time-based demurrage. Total Dem still includes the
        // pump count (handled live), so the demurrage calculation is unchanged.
        const entryData = { ...formData, pumpEnabled, pumpCount };

        const calculatedEntry = {
          ...entryData,
          freightRev,
          fuelSurcharge: fuelSurcharge.toString(),
          demurrageCharge,
          totalCharges
        };

        if (editingIndex !== null) {
          onUpdateEntry(editingIndex, entryData, calculatedEntry);
          setEditingIndex(null);
        } else {
          onAddEntry(entryData, calculatedEntry);
        }

        // Reset form
        setFormData(prev => ({
          ...defaultInvoiceItem,
          broker: prev.broker, // Preserve broker selection
          invoiceSheet: prev.invoiceSheet, // Preserve invoice sheet selection
          rowNumber: editingIndex !== null ? formData.rowNumber : nextRowNumber.toString(),
          // Preserve invoice information fields
          invoiceDate: prev.invoiceDate,
          dueDate: prev.dueDate,
          miles: prev.miles,
          customer: prev.customer,
          address: prev.address,
          cityStateZip: prev.cityStateZip
        }));
        resetPumpState();

        // Only increment row number if not editing
        if (editingIndex === null) {
          setNextRowNumber(prev => prev + 1);
        }

        scrollFormToTop();
      } catch (submitError) {
        setError(submitError instanceof Error ? submitError.message : 'An unknown error occurred');
      }
    };

    const handleEditEntry = (index: number) => {
      const entryToEdit = entries[index];
      if (!entryToEdit) return;
      setFormData(entryToEdit);
      // Restore the pump charge from the entry. Total Dem is stored combined, so the
      // base (time demurrage) is the stored Total Dem minus the pump contribution.
      const enabled = entryToEdit.pumpEnabled ?? false;
      const count = entryToEdit.pumpCount ?? '1';
      const pumps = enabled ? parseFloat(count) || 0 : 0;
      const combined = parseFloat(entryToEdit.totalDem || '0') || 0;
      setPumpEnabled(enabled);
      setPumpCount(count);
      setBaseDem(entryToEdit.totalDem === '' && pumps === 0 ? '' : String(combined - pumps));
      setEditingIndex(index);
      scrollFormToTop();
    };

    const handleCancel = () => {
      if (editingIndex !== null) {
        setEditingIndex(null);
      }
      setFormData(prev => ({
        ...defaultInvoiceItem,
        broker: prev.broker,
        invoiceSheet: prev.invoiceSheet,
        rowNumber: nextRowNumber.toString()
      }));
      resetPumpState();
    };

    const handleGenerateExcel = async () => {
      setError(null);

      if (entries.length === 0) {
        setError('Please add at least one entry before generating Excel files');
        return;
      }

      try {
        await onSubmit();
        // Reset form state
        setNextRowNumber(5);
        setFormData(prev => ({
          ...defaultInvoiceItem,
          broker: prev.broker,
          invoiceSheet: prev.invoiceSheet,
          rowNumber: '4'
        }));
        resetPumpState();
        onClearEntries();
      } catch (submitError) {
        setError(submitError instanceof Error ? submitError.message : 'An unknown error occurred');
      }
    };

    const populateDebugData = () => {
      const debugData = {
        ...formData, // Preserve all existing form data, including invoice information
        rowNumber: formData.rowNumber,
        destination: 'Los Angeles, CA',
        date: new Date().toISOString().split('T')[0],
        bolNumber: `BOL-${Math.floor(Math.random() * 10000)}`,
        invoiceNumber: `INV-${Math.floor(Math.random() * 10000)}`,
        orderNumber: `ORD-${Math.floor(Math.random() * 10000)}`,
        loadNumber: `LD-${Math.floor(Math.random() * 10000)}`,
        terminal: terminals[Math.floor(Math.random() * terminals.length)],
        gross: '8000',
        net: '7989',
        rate: '0.1',
        fuelSurchargePercent: '54',
        terminalArrivalTime: '08:00',
        terminalDepartTime: '09:00',
        deliveryArrivalTime: '10:00',
        deliveryDepartTime: '11:00',
        driver: 'John Doe',
        notes: 'Debug data',
        totalDem: '2'
      };
      setFormData(debugData);
      setPumpEnabled(false);
      setPumpCount('1');
      setBaseDem('2');
    };

    const handleCustomerSelect = (event: SelectChangeEvent) => {
      const selectedCustomer = customerLocations.find(loc => loc.name === event.target.value);
      if (selectedCustomer) {
        setFormData(prev => ({
          ...prev,
          customer: selectedCustomer.name,
          miles: selectedCustomer.miles,
          address: selectedCustomer.address,
          cityStateZip: selectedCustomer.cityStateZip
        }));
      }
    };

    useImperativeHandle(ref, () => ({
      resetForm: () => {
        setFormData(prev => ({
          ...defaultInvoiceItem,
          broker: prev.broker,
          invoiceSheet: prev.invoiceSheet,
          rowNumber: nextRowNumber.toString()
        }));
        resetPumpState();
      },
      setFormData: (data: InvoiceItem) => {
        // Preserve the current rowNumber - it should NOT be overwritten by external data
        setFormData(prev => ({ ...data, rowNumber: prev.rowNumber }));
        setPumpEnabled(false);
        setPumpCount('1');
        setBaseDem(data.totalDem || '');
      },
      handleEditEntry,
      handleDeleteEntry: (index: number) => onDeleteEntry(index)
    }));

    /* ------------------------------- shortcuts ------------------------------- */

    useEffect(() => {
      const onKeyDown = (event: KeyboardEvent) => {
        const meta = event.metaKey || event.ctrlKey;

        if (meta && event.key.toLowerCase() === 's') {
          event.preventDefault();
          onSaveDraft();
          return;
        }
        if (meta && event.key === 'Enter') {
          event.preventDefault();
          handleAddEntry(event as unknown as FormEvent);
          return;
        }
        if (event.key === 'Escape' && editingIndex !== null) {
          handleCancel();
        }
      };

      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSaveDraft, editingIndex, formData, pumpEnabled, pumpCount, nextRowNumber, entries]);

    /* -------------------------------- helpers -------------------------------- */

    // A green tick marks a required field that now has a value.
    const completeAdornment = (field: keyof InvoiceItem) =>
      isFieldComplete(formData, field) ? (
        <InputAdornment position="end">
          <CheckCircleIcon sx={{ fontSize: 18, color: '#16A34A' }} />
        </InputAdornment>
      ) : undefined;

    const textField = (
      field: keyof InvoiceItem,
      label: string,
      options: {
        required?: boolean;
        type?: string;
        placeholder?: string;
        multiline?: boolean;
        rows?: number;
      } = {}
    ) => (
      <TextField
        fullWidth
        size="small"
        label={label}
        name={field}
        type={options.type}
        required={options.required}
        placeholder={options.placeholder}
        multiline={options.multiline}
        rows={options.rows}
        value={(formData[field] as string) ?? ''}
        onChange={handleChange}
        InputLabelProps={
          options.type === 'date' || options.type === 'time' ? { shrink: true } : undefined
        }
        InputProps={options.required ? { endAdornment: completeAdornment(field) } : undefined}
        // Phones get the number pad for numeric fields.
        inputProps={options.type === 'number' ? { inputMode: 'decimal' } : undefined}
      />
    );

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: { md: '100%' },
          width: '100%',
          minWidth: 0
        }}
      >
        {!isMobile && (
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography variant="subtitle1">Invoice Entry</Typography>
          </Box>
        )}

        <EntrySteps
          activeIndex={activeSection}
          completeIds={completeSectionIds}
          onSelect={scrollToSection}
          compact={isMobile}
          stickyTop={MOBILE_APP_BAR_HEIGHT}
        />
        {!isMobile && <Divider />}

        {/* Scrolling form body — this pane scrolls independently of the PDF */}
        <Box
          component="form"
          id="invoice-entry-form"
          onSubmit={handleAddEntry}
          ref={scrollRef}
          sx={{
            flex: { md: 1 },
            overflowY: { md: 'auto' },
            px: 2,
            pt: { xs: 2.5, md: 2 },
            pb: { xs: 1, md: 2 }
          }}
        >
          {editingIndex !== null && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Editing entry {editingIndex + 1}. Press Update Entry to save, or Cancel to discard.
            </Alert>
          )}

          {/* 1 — Basic Information */}
          <SectionHeading id={ENTRY_SECTIONS[0].id} title={ENTRY_SECTIONS[0].title} first />
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 6 }}>
              {textField('rowNumber', isMobile ? 'Excel Row' : 'Excel Row Number', { required: true, type: 'number' })}
            </Grid>
            <Grid size={{ xs: 6, sm: 6 }}>
              {textField('date', 'Date', { required: true, type: 'date' })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('orderNumber', 'Order Number', { required: true })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('loadNumber', 'Load Number', { required: true })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('invoiceNumber', 'Invoice Number', { required: true })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('destination', 'Destination', { required: true })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('bolNumber', 'BOL Number', {
                required: true,
                placeholder: 'Enter BOL Number'
              })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small" required>
                <InputLabel id="terminal-label">Terminal</InputLabel>
                <Select
                  labelId="terminal-label"
                  name="terminal"
                  value={formData.terminal}
                  onChange={handleChange}
                  label="Terminal"
                >
                  {terminals.map(terminal => (
                    <MenuItem key={terminal} value={terminal}>
                      {terminal}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>{textField('driver', 'Driver', { required: true })}</Grid>
          </Grid>

          {/* 2 — Amounts */}
          <SectionHeading id={ENTRY_SECTIONS[1].id} title={ENTRY_SECTIONS[1].title} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 4 }}>
              {textField('gross', 'Gross', { required: true, type: 'number' })}
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              {textField('net', 'Net', { required: true, type: 'number' })}
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              {textField('rate', 'Rate', { required: true, type: 'number' })}
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                // The % adornment says the unit; the short label fits a half-width field.
                label={isMobile ? 'Fuel Surcharge' : 'Fuel Surcharge %'}
                name="fuelSurchargePercent"
                type="number"
                required
                value={formData.fuelSurchargePercent}
                onChange={handleChange}
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
          </Grid>

          {/* 3 — Terminal & Delivery Times */}
          <SectionHeading id={ENTRY_SECTIONS[2].id} title={ENTRY_SECTIONS[2].title} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('terminalArrivalTime', 'Terminal Arrival Time', {
                required: true,
                type: 'time'
              })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('terminalDepartTime', 'Terminal Depart Time', {
                required: true,
                type: 'time'
              })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('deliveryArrivalTime', 'Delivery Arrival Time', {
                required: true,
                type: 'time'
              })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('deliveryDepartTime', 'Delivery Depart Time', {
                required: true,
                type: 'time'
              })}
            </Grid>
          </Grid>

          {/* 4 — Notes & Summary */}
          <SectionHeading id={ENTRY_SECTIONS[3].id} title={ENTRY_SECTIONS[3].title} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              {textField('notes', 'Notes', {
                multiline: true,
                rows: 2,
                placeholder: 'Enter any additional notes...'
              })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Total Dem"
                name="totalDem"
                type="number"
                placeholder="Enter total dem"
                value={formData.totalDem}
                onChange={handleTotalDemChange}
                InputProps={{ readOnly: pumpEnabled }}
                inputProps={{ inputMode: 'decimal' }}
                helperText={
                  pumpEnabled
                    ? `Base ${baseDem || 0} + ${parseFloat(pumpCount) || 0} pump(s)`
                    : undefined
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 40 }}>
                <FormControlLabel
                  control={<Checkbox checked={pumpEnabled} onChange={handlePumpToggle} />}
                  label="Pump Charge"
                />
                {pumpEnabled && (
                  <TextField
                    size="small"
                    label="Number of Pumps"
                    name="pumpCount"
                    type="number"
                    value={pumpCount}
                    onChange={handlePumpCountChange}
                    inputProps={{ min: 1, inputMode: 'numeric' }}
                    sx={{ maxWidth: 170 }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>

          {/* 5 — Invoice Information */}
          <SectionHeading id={ENTRY_SECTIONS[4].id} title={ENTRY_SECTIONS[4].title} />
          <Grid container spacing={2} sx={{ pb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="customer-select-label">Customer Select</InputLabel>
                <Select
                  labelId="customer-select-label"
                  value={formData.customer}
                  onChange={handleCustomerSelect}
                  label="Customer Select"
                >
                  {customerLocations.map(location => (
                    <MenuItem key={location.name} value={location.name}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('customer', 'Customer', {
                placeholder: 'Enter customer name or select from dropdown'
              })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('invoiceDate', 'Invoice Date', { type: 'date' })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>{textField('dueDate', 'Due Date', { type: 'date' })}</Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('miles', 'Miles', { type: isMobile ? 'number' : undefined })}
            </Grid>
            <Grid size={{ xs: 12 }}>{textField('address', 'Address')}</Grid>
            <Grid size={{ xs: 12 }}>{textField('cityStateZip', 'City, State, ZIP')}</Grid>
          </Grid>
        </Box>

        {/* Actions — phones get a thumb-reachable bar pinned to the bottom */}
        {isMobile ? (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 6,
              backgroundColor: 'background.paper',
              borderTop: `1px solid ${BRAND.border}`,
              boxShadow: '0 -8px 18px -12px rgba(15, 23, 42, 0.45)',
              px: 1.5,
              pt: 1.25,
              pb: 'calc(10px + env(safe-area-inset-bottom))'
            }}
          >
            {entries.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <Button
                  size="small"
                  startIcon={
                    <Badge badgeContent={entries.length} color="primary" max={99}>
                      <TableChartOutlinedIcon fontSize="small" />
                    </Badge>
                  }
                  onClick={onPreviewEntries}
                  sx={{ flex: 1, justifyContent: 'flex-start', pl: 1.5, minHeight: 36 }}
                >
                  &nbsp;Review entries
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<GridOnIcon />}
                  onClick={handleGenerateExcel}
                  disabled={isSaving}
                  sx={{ minHeight: 36, flexShrink: 0 }}
                >
                  {isSaving ? 'Saving...' : 'Generate Excel'}
                </Button>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {onShowDocument && (
                <IconButton
                  aria-label="View PDF"
                  onClick={onShowDocument}
                  sx={{
                    width: 48,
                    height: 48,
                    flexShrink: 0,
                    borderRadius: '10px',
                    border: `1px solid ${BRAND.border}`,
                    color: BRAND.blue
                  }}
                >
                  <PictureAsPdfOutlinedIcon />
                </IconButton>
              )}
              <IconButton
                aria-label="More actions"
                onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
                sx={{
                  width: 48,
                  height: 48,
                  flexShrink: 0,
                  borderRadius: '10px',
                  border: `1px solid ${BRAND.border}`
                }}
              >
                <MoreVertIcon />
              </IconButton>
              {editingIndex !== null && (
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ height: 48, flexShrink: 0, color: 'text.secondary', borderColor: BRAND.border }}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                form="invoice-entry-form"
                variant="contained"
                sx={{ flex: 1, height: 48, fontSize: 16, borderRadius: '10px' }}
              >
                {editingIndex !== null ? 'Update Entry' : 'Add Entry'}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
          {/* Actions */}
          <Divider />
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1.25,
              px: 2,
              py: 1.5
            }}
          >
            <Button onClick={handleCancel} sx={{ color: 'text.secondary' }}>
              Cancel
            </Button>
            <Button variant="outlined" startIcon={<SaveOutlinedIcon />} onClick={onSaveDraft}>
              Save Draft
            </Button>
            <Button type="submit" form="invoice-entry-form" variant="contained">
              {editingIndex !== null ? 'Update Entry' : 'Add Entry'}
            </Button>

            <ButtonGroup variant="outlined" color="success" disabled={isSaving}>
              <Button
                startIcon={<GridOnIcon />}
                onClick={handleGenerateExcel}
                disabled={isSaving || entries.length === 0}
              >
                {isSaving ? 'Saving...' : 'Generate Excel'}
              </Button>
              <Button size="small" onClick={(e) => setGenerateMenuAnchor(e.currentTarget)}>
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>

            <Tooltip title="Populate with debug data">
              <Button variant="outlined" startIcon={<BugReportIcon />} onClick={populateDebugData}>
                Debug
              </Button>
            </Tooltip>
          </Box>

          <ShortcutsBar />
          </>
        )}

        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={() => setMobileMenuAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { minWidth: 220 } } }}
        >
          <MenuItem
            onClick={() => {
              setMobileMenuAnchor(null);
              onSaveDraft();
            }}
          >
            <ListItemIcon>
              <SaveOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Save draft</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={entries.length === 0}
            onClick={() => {
              setMobileMenuAnchor(null);
              onPreviewEntries();
            }}
          >
            <ListItemIcon>
              <TableChartOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Review entries ({entries.length})</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={entries.length === 0 || isSaving}
            onClick={() => {
              setMobileMenuAnchor(null);
              handleGenerateExcel();
            }}
          >
            <ListItemIcon>
              <GridOnIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Generate Excel</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              setMobileMenuAnchor(null);
              handleCancel();
            }}
          >
            <ListItemIcon>
              <RestartAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Clear form</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={entries.length === 0}
            onClick={() => {
              setMobileMenuAnchor(null);
              onClearEntries();
            }}
          >
            <ListItemIcon />
            <ListItemText>Clear all entries</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMobileMenuAnchor(null);
              populateDebugData();
            }}
          >
            <ListItemIcon>
              <BugReportIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Fill debug data</ListItemText>
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={generateMenuAnchor}
          open={Boolean(generateMenuAnchor)}
          onClose={() => setGenerateMenuAnchor(null)}
        >
          <MenuItem
            disabled={entries.length === 0}
            onClick={() => {
              setGenerateMenuAnchor(null);
              onPreviewEntries();
            }}
          >
            <ListItemText>Preview entries ({entries.length})</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={entries.length === 0}
            onClick={() => {
              setGenerateMenuAnchor(null);
              onClearEntries();
            }}
          >
            <ListItemText>Clear all entries</ListItemText>
          </MenuItem>
        </Menu>

        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ bottom: { xs: 'calc(132px + env(safe-area-inset-bottom))', md: 24 } }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
);

InvoiceEntryForm.displayName = 'InvoiceEntryForm';

export default InvoiceEntryForm;
